import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  LANGUAGES,
  LANGUAGE_NAMES,
  changeLanguage,
  onLoadingChange,
  type Language,
} from '@/i18n'

interface LanguageSelectorProps {
  className?: string
}

/**
 * Language selector dropdown with loading state support.
 *
 * Shows loading indicator when lazy-loading German or Spanish translations.
 *
 * @example
 * <LanguageSelector className="w-32" />
 */
export function LanguageSelector({ className }: LanguageSelectorProps) {
  const { i18n } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)

  useEffect(() => {
    return onLoadingChange(setIsLoading)
  }, [])

  const handleLanguageChange = (lang: string) => {
    changeLanguage(lang as Language)
  }

  // Normalize language code (e.g., 'en-US' → 'en')
  const currentLang = i18n.language?.split('-')[0] as Language
  const selectedLang = LANGUAGES.includes(currentLang) ? currentLang : 'en'

  return (
    <Select
      value={selectedLang}
      onValueChange={handleLanguageChange}
      disabled={isLoading}
    >
      <SelectTrigger className={className}>
        {isLoading ? (
          <span className="text-muted-foreground">Loading...</span>
        ) : (
          <SelectValue />
        )}
      </SelectTrigger>
      <SelectContent>
        {LANGUAGES.map((lang) => (
          <SelectItem key={lang} value={lang}>
            {LANGUAGE_NAMES[lang]}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
}

export default LanguageSelector

